import {
  Accordion,
  AccordionButtonProps,
  Box,
  BoxProps,
  Center,
  SimpleGrid,
  SimpleGridProps,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React, { ReactNode } from "react";

import { useScrollbarWidth, useTableCheckboxes } from "@nens/react/hooks";

import { QuickRowAction, RowAction } from "./row-actions-menu";
import { SelectTableAction } from "./select-table-action";
import { TableHeader } from "./table-header";
import { TableRow } from "./table-row";

export type ColumnOrdering = "az" | "za" | undefined;

export interface ColumnDefinition<TableRowType> {
  title?: string;
  titleRenderFunction?: () => string | React.ReactNode;
  renderFunction: (row: TableRowType) => React.ReactNode | string | number;
  orderingField?: string;
  width: string;
  align?: "left" | "right" | "center"; // Default left
}

export interface TableAction<TableRowType> {
  label: string;
  action: (rows: TableRowType[]) => void;
  isDisabledTitle?: string;
  icon?: React.ReactNode;
}

export interface TableProps<TableRowType> extends BoxProps {
  columnDefinitions: ColumnDefinition<TableRowType>[];
  tableData: TableRowType[];

  defaultSelected?: { uuid: string }[];
  sendSelected?: (selected: { uuid: string }[]) => void;
  isLoading?: boolean;
  buttonVariant?: string;
  quickRowAction?: QuickRowAction<TableRowType>;
  tableActions?: TableAction<TableRowType>[];
  rowActions?: RowAction<TableRowType>[];

  ordering?: { [fieldName: string]: ColumnOrdering };
  setOrdering?: (fieldName: string, ordering: ColumnOrdering) => void;

  tableRowGridProps?: SimpleGridProps;
  rowAccordion?: (row: TableRowType) => ReactNode;
  accordionButtonProps?: AccordionButtonProps;

  spacing?: string;
  lineHeight?: string;
  themeColor?: string;
  borderColor?: string;

  emptyText?: string;
}

export function Table<TableRowType extends { uuid: string }>(props: TableProps<TableRowType>) {
  const {
    columnDefinitions,
    tableData,
    isLoading = false,
    width,
    height = "616px",
    quickRowAction,
    rowActions = [],
    tableActions = [],
    defaultSelected,
    sendSelected,
    ordering,
    setOrdering,
    spacing = "1rem",
    lineHeight = "40px",
    emptyText = "There is no data to be shown with the current filters.",
    buttonVariant = "outline",
    themeColor = "blue.700",
    borderColor = themeColor,
    rowAccordion,
    accordionButtonProps,
    tableRowGridProps,
    ...rest
  } = props;
  const hasRowActions = Boolean(rowActions && rowActions.length);
  const hasQuickRowAction = Boolean(quickRowAction);
  const hasTableActions = Boolean(tableActions && tableActions.length);
  const hasSendSelection = Boolean(defaultSelected || sendSelected);
  const hasSelection = hasTableActions || hasSendSelection;

  const [tableRef, scrollbarWidth] = useScrollbarWidth();

  const gridTemplateColumns = columnDefinitions
    .map((definition) => `${definition.width}`)
    .join(" ");

  const allTemplateColumns = `${hasSelection ? "50px " : ""} ${
    hasQuickRowAction ? "50px " : ""
  }${gridTemplateColumns}${hasRowActions ? " 50px" : ""}`;
  const columns = (hasSelection ? 1 : 0) + columnDefinitions.length + (hasRowActions ? 1 : 0);

  const {
    uuidChecked,
    setChecked,
    checkedRows,
    allChecked,
    someChecked,
    deselectAll,
    setAllChecked,
  } = useTableCheckboxes(tableData, defaultSelected, sendSelected);

  return (
    <Box {...rest}>
      {hasTableActions ? (
        <SelectTableAction
          tableActions={tableActions}
          deselectAll={deselectAll}
          rows={tableData.filter((row) => checkedRows.some((check) => check.uuid === row.uuid))}
        />
      ) : null}
      <SimpleGrid
        columns={columns}
        templateColumns={
          scrollbarWidth > 0
            ? `${allTemplateColumns} calc(${scrollbarWidth}px - ${spacing})`
            : allTemplateColumns
        }
        spacing={`0.1rem ${spacing}`}
        padding={`0.5rem ${spacing}`}
        boxShadow="2px 2px 2px #00000029"
        backgroundColor={themeColor}
        color="gray.100"
      >
        <TableHeader
          themeColor={themeColor}
          columnDefinitions={columnDefinitions}
          hasQuickRowAction={hasQuickRowAction}
          hasRowActions={hasRowActions}
          hasSelection={hasSelection}
          someChecked={someChecked}
          allChecked={allChecked}
          setAllChecked={setAllChecked}
          ordering={ordering}
          setOrdering={setOrdering}
          hasScrollbar={scrollbarWidth > 0}
        />
      </SimpleGrid>
      <Box
        height={height}
        overflowY="auto"
        overflowX="hidden"
        ref={tableRef}
        borderLeft="solid 1px"
        borderRight="solid 1px"
        borderBottom="solid 1px"
        borderColor={borderColor}
        boxShadow="2px 2px 2px #00000029"
      >
        {!isLoading ? (
          tableData.length > 0 ? (
            <Accordion allowToggle>
              {tableData.map((row, rowIdx) => (
                <TableRow
                  gridProps={{
                    columns: columns,
                    templateColumns: allTemplateColumns,
                    boxSizing: "border-box",
                    spacing: `0.1rem ${spacing}`,
                    padding: `0.5rem ${spacing}`,
                    ...tableRowGridProps,
                  }}
                  key={`table-row-${rowIdx}`}
                  rowData={row}
                  buttonVariant={buttonVariant}
                  columnDefinitions={columnDefinitions}
                  hasSelection={hasSelection}
                  hasQuickRowAction={hasQuickRowAction}
                  quickRowAction={quickRowAction}
                  hasRowActions={hasRowActions}
                  selected={uuidChecked(row.uuid)}
                  setSelected={(event) => {
                    setChecked(row.uuid, event);
                  }}
                  rowActions={rowActions}
                  lineHeight={lineHeight}
                  rowAccordion={rowAccordion}
                  accordionButtonProps={accordionButtonProps}
                />
              ))}
            </Accordion>
          ) : (
            <Center textAlign="center" width="100%" height="100%">
              <Text my={4}>{emptyText}</Text>
            </Center>
          )
        ) : (
          <Center width="100%" height="100%">
            <Spinner size="xl" my={4} />
          </Center>
        )}
      </Box>
    </Box>
  );
}
